import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
    InputOTP, InputOTPGroup, InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Mail, Phone, ShieldCheck, CheckCircle2, Loader2, Store, FlaskConical } from "lucide-react";

function StepBadge({ done, active, n }) {
    return (
        <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${done ? "border-success bg-success text-white" : active ? "border-primary bg-accent text-accent-foreground" : "border-border bg-muted text-muted-foreground"}`}>
            {done ? <CheckCircle2 className="h-4 w-4" /> : n}
        </span>
    );
}

// Persistent, clearly-labeled callout showing a code while integrations run in test mode.
function DevCodeCallout({ label, code }) {
    if (!code) return null;
    return (
        <div data-testid="dev-code-callout" className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-warning"><FlaskConical className="h-3.5 w-3.5" />{label}</span>
            <code className="select-all font-mono text-sm font-semibold tracking-wider text-warning">{code}</code>
        </div>
    );
}

export default function VerifyAccount() {
    const { user, refreshUser, config } = useAuth();
    const navigate = useNavigate();
    const [emailCode, setEmailCode] = useState("");
    const [phone, setPhone] = useState(user?.phone || "+1");
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState("");
    const [busy, setBusy] = useState("");
    const [devEmail, setDevEmail] = useState("");
    const [devOtp, setDevOtp] = useState("");

    // A user is fully verified when email + phone are done, and (for sellers) KYC is complete.
    const sellerRole = user && (user.role === "seller" || user.role === "both");
    const fullyVerified = !!user && user.email_verified && user.phone_verified
        && (!sellerRole || user.seller_status === "VERIFIED");
    const dashboard = sellerRole ? "/dashboard/seller" : "/dashboard/buyer";

    // Guard: skip this screen once there's nothing left to verify.
    useEffect(() => {
        if (fullyVerified) navigate(dashboard, { replace: true });
    }, [fullyVerified, dashboard, navigate]);

    if (!user) { navigate("/login"); return null; }
    if (fullyVerified) return null;

    const twilioReal = config?.twilio_enabled;
    const stripeReal = config?.stripe_connect_real;

    const verifyEmail = async () => {
        setBusy("email");
        try {
            await api.post("/auth/verify-email", { token: emailCode });
            await refreshUser();
            setDevEmail("");
            toast.success("Email verified");
        } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); } finally { setBusy(""); }
    };
    const resendEmail = async () => {
        try {
            const { data } = await api.post("/auth/resend-email");
            if (data.dev_email_token) { setDevEmail(data.dev_email_token); toast.success("New code generated"); }
            else toast.success("Verification email sent");
        } catch (e) { toast.error("Could not resend"); }
    };
    const sendOtp = async () => {
        setBusy("otp-send");
        try {
            const { data } = await api.post("/auth/send-otp", { phone });
            setOtpSent(true);
            if (data.dev_otp) { setDevOtp(data.dev_otp); toast.success("Test code generated"); }
            else { setDevOtp(""); toast.success("OTP sent via SMS"); }
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not send OTP"); } finally { setBusy(""); }
    };
    const verifyOtp = async () => {
        setBusy("otp-verify");
        try {
            await api.post("/auth/verify-otp", { phone, code: otp });
            await refreshUser();
            setDevOtp("");
            toast.success("Phone verified");
        } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); } finally { setBusy(""); }
    };
    const startConnect = async () => {
        setBusy("connect");
        try {
            const { data } = await api.post("/seller/connect/onboard");
            if (data.mode === "real") { window.location.href = data.url; return; }
            await api.post("/seller/connect/complete");
            await refreshUser();
            toast.success("Seller verification complete");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start onboarding"); } finally { setBusy(""); }
    };

    const emailDone = user.email_verified;
    const phoneDone = user.phone_verified;
    const sellerDone = user.seller_status === "VERIFIED";

    return (
        <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
            <h1 className="font-display text-3xl font-semibold tracking-tight">Welcome to OmniAuc</h1>
            <p className="mt-1 text-sm text-muted-foreground">Two quick steps and you're ready to bid &mdash; three if you want to sell. Email and phone are required before bidding.</p>

            <div data-testid="verification-stepper" className="mt-8 space-y-4">
                {/* Email */}
                <Card data-testid="verification-stepper-step" className="p-5">
                    <div className="flex items-start gap-3">
                        <StepBadge done={emailDone} active={!emailDone} n={1} />
                        <div className="flex-1">
                            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-electric" /><h3 className="font-display font-semibold">Verify email</h3></div>
                            <p className="mt-0.5 text-xs text-muted-foreground">We sent you a code &mdash; enter it to confirm it's really you.</p>
                            {emailDone ? <p className="mt-1 text-sm text-success">Email verified ✓</p> : (
                                <>
                                    <div className="mt-3 flex flex-wrap items-end gap-2">
                                        <div className="space-y-1"><Label htmlFor="ecode" className="text-xs">Verification code</Label><Input id="ecode" data-testid="verify-email-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} className="w-40" placeholder="ABCD1234" /></div>
                                        <Button onClick={verifyEmail} disabled={busy === "email" || !emailCode} className="rounded-[10px]" data-testid="verify-email-submit">{busy === "email" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify</Button>
                                        <Button variant="ghost" onClick={resendEmail} className="rounded-[10px]">Resend code</Button>
                                    </div>
                                    <DevCodeCallout label="Test mode — email code" code={devEmail} />
                                </>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Phone */}
                <Card data-testid="verification-stepper-step" className="p-5">
                    <div className="flex items-start gap-3">
                        <StepBadge done={phoneDone} active={emailDone && !phoneDone} n={2} />
                        <div className="flex-1">
                            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-electric" /><h3 className="font-display font-semibold">Verify phone</h3>{!twilioReal && <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] text-warning"><FlaskConical className="h-2.5 w-2.5" />Test mode</span>}</div>
                            <p className="mt-0.5 text-xs text-muted-foreground">We'll text a 6-digit code. Phone verification keeps bidding fair and bots out.</p>
                            {phoneDone ? <p className="mt-1 text-sm text-success">Phone verified ✓</p> : (
                                <div className="mt-3 space-y-3">
                                    <div className="flex flex-wrap items-end gap-2">
                                        <div className="space-y-1"><Label htmlFor="phone" className="text-xs">Phone (E.164)</Label><Input id="phone" data-testid="verify-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-48" placeholder="+14155552671" /></div>
                                        <Button onClick={sendOtp} disabled={busy === "otp-send"} className="rounded-[10px]" data-testid="send-otp-button">{busy === "otp-send" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send code</Button>
                                    </div>
                                    {otpSent && (
                                        <>
                                            <div className="flex flex-wrap items-end gap-3">
                                                <div className="space-y-1"><Label className="text-xs">Enter 6-digit code</Label>
                                                    <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="verify-otp-input">
                                                        <InputOTPGroup>
                                                            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                                                        </InputOTPGroup>
                                                    </InputOTP>
                                                </div>
                                                <Button onClick={verifyOtp} disabled={busy === "otp-verify" || otp.length < 6} className="rounded-[10px]" data-testid="verify-otp-submit">{busy === "otp-verify" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify phone</Button>
                                            </div>
                                            <DevCodeCallout label="Test mode — SMS code" code={devOtp} />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Seller KYC */}
                <Card data-testid="verification-stepper-step" className="p-5">
                    <div className="flex items-start gap-3">
                        <StepBadge done={sellerDone} active={phoneDone && !sellerDone} n={3} />
                        <div className="flex-1">
                            <div className="flex items-center gap-2"><Store className="h-4 w-4 text-electric" /><h3 className="font-display font-semibold">Become a verified seller</h3>{!stripeReal && <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] text-warning"><FlaskConical className="h-2.5 w-2.5" />Simulated</span>}</div>
                            <p className="mt-1 text-xs text-muted-foreground">Complete a quick identity check (Stripe Connect) to start listing and get paid. Only needed if you want to sell.{!stripeReal && " Completes instantly in test mode."}</p>
                            {sellerDone ? <p className="mt-1 text-sm text-success">Seller verification complete ✓</p> : (
                                <Button onClick={startConnect} disabled={busy === "connect" || !phoneDone} className="mt-3 rounded-[10px]" data-testid="connect-onboard-button">
                                    {busy === "connect" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<ShieldCheck className="mr-2 h-4 w-4" />Complete seller verification
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            <div className="mt-8 flex gap-3">
                <Button variant="secondary" className="rounded-[10px]" onClick={() => navigate("/auctions")}>Browse auctions</Button>
                {sellerDone && <Button className="rounded-[10px]" onClick={() => navigate("/dashboard/seller")}>Go to seller dashboard</Button>}
            </div>
        </div>
    );
}
